import { NextPage } from 'next'
import { CAREERS, FAQs } from 'routes/external'

import styles from 'components/about/about.module.scss'

import { MetaTags } from '~shared'
import { Grid, GlowHeading, Heading, Icon, Layout, Typography, Image, Button, NeonBox } from '~ui'
import NeonCard from '~ui/NeonCard'
import classNames from 'classnames'
import SlideshowGallery from '~shared/SlideshowGalleryV2'
import Reviews from 'components/home/block/Reviews'

const reviewsData = [
  {
    bodyText:
      'Granted not only helped me book a call with my childhood hero, but it also allowed me to give to great cause in order to get the job done. Overall great experience!',
    customer: {
      imageSrc: 'https://img.granted.co/1644593862027-review-1.png',
      name: 'Travis C.',
      role: 'Auction Winner',
    },
  },
  {
    bodyText:
      'I bought tickets to The Rolling Stones and it was a seamless, easy process. I had questions about the tickets before the show and Granted’s customer support was super helpful and quick to respond. I would definitely buy from here again.',
    customer: {
      imageSrc: 'https://img.granted.co/1644593910891-review-2.png',
      name: 'Grant T.',
      role: 'Granted Customer',
    },
  },
  {
    bodyText:
      'I recently had the pleasure of going to my Saquon Barkley meet and greet from Granted. From start to finish, the process as a whole was beyond fantastic...I can’t thank Granted and my game day concierge enough, for one of the best days of my life and for allowing me to make one of my dads dreams come true!',
    customer: {
      imageSrc: 'https://img.granted.co/1644593934158-review-3.png',
      name: 'David A.',
      role: 'Auction Winner',
    },
  },
]

const About: NextPage = () => (
  <>
    <MetaTags title="Who We Are" />
    <Layout.Banner />
    <Layout.HomeHeader />
    <Layout.Main withSidebar={false}>
      <div className={styles['about-page']}>
        <Grid className={styles.hero}>
          <Grid gap={32} height="fit-content" margin="auto">
            <Icon name="lightning" width="4em" height="4em" glow glowColor="g-green-dark" />
            <GlowHeading Tag="h2" size="h1" color="g-green-darker">
              PAST EXPERIENCES
            </GlowHeading>
            <Typography.Text>
              Check out Granted’s exclusive partnerships, auctions and events to get a better feel
              on what we’re all about.
            </Typography.Text>
          </Grid>
          <SlideshowGallery
            images={[
              {
                url: 'https://img.granted.co/600x400/1655818436721-hamilton.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818471806-chainsmokers.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818493463-kygo.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818515945-shaq.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818536764-steve-nelson.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818555114-saqulon.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818582431-revels-group.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818600742-logan-paul.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818624979-bts.jpg',
              },
              {
                url: 'https://img.granted.co/600x400/1655818641233-rotimi.jpg',
              },
            ]}
            interval={4}
          />
        </Grid>

        <div className={styles.contact}>
          <NeonBox title="Want to work with us? Let's do it.">
            <Button
              className={styles['buttons-style']}
              uppercase
              variant="primary"
              mode="external-link"
              icon="twitch"
              clr="g-green-darker"
              href="mailto:partnerships@granted.co"
            >
              Contact Us
            </Button>
          </NeonBox>
        </div>

        <Grid className={styles.description}>
          <Heading Tag="h2" colorGlow="light-purple" uppercase fontWeight={300}>
            Who we are
          </Heading>
          <Typography.Text>
            We are a new ticketing platform and experience provider dedicated to creating deeper,
            more meaningful connections between artists and fans. And we care a lot about that first
            sentence. Like A LOT. Because we’re just fans—like you. And we wanted to create a place
            where people didn’t just feel like another sale when buying their tickets. So we created
            Granted.
          </Typography.Text>
          <Typography.Text>
            At Granted, you can purchase VIP packages curated by your favorite artists, bid on
            unforgettable auctions that benefit important causes, and get tickets to awesome live
            events. Look, we know we aren’t the first, and we know the purchase to delivery process
            can have it&apos;s ‘WTF’ moments. But here, we will always have your back to ensure you
            have the best experience when going with Granted. Our Fan Promise guarentees your
            tickets, and our customer support team will actually get back to you (unlike some of the
            other guys). So what are you waiting for? Let’s get your access, Granted.
          </Typography.Text>
        </Grid>

        <Grid className={styles.cards}>
          <NeonCard
            iconName="vip-pink"
            title="Vip Packages"
            colorGlow="pink"
            description="Carefully created in partnership with your favorite artists to create custom, exclusive experiences including meet & greets and unique merch to pre-show events that add that extra va-va-voom to your night."
          />
          <NeonCard
            iconName="diamond"
            title="Auctions"
            colorGlow="blue"
            description="Unlock access to the most incredible once in a lifetime experiences in sports, music, and entertainment. And the best part? They all benefit important charities and causes."
          />
          <NeonCard
            iconName="live"
            title="Live Events"
            colorGlow="teal"
            description="The Granted platform is easy to use, carefully monitored, and cool to look at (if we do say so, ourselves). We work with trusted third-parties for tickets. And any time you have a question, a REAL person will be on the other end."
          />
        </Grid>

        <Reviews reviews={reviewsData} />

        <Grid className={classNames(styles.hero, styles['even-layout'])}>
          <Grid gap={32} height="fit-content" margin="auto">
            <Heading Tag="h2" colorGlow="g-green-darker" fontWeight={300} uppercase>
              Meet The team
            </Heading>
            <Typography.Text>
              Granted is a small but mighty team of individuals who would rather miss a family’s
              birthday than their favorite concert, can rattle off sports statistics like their life
              depends on it, and knows the hottest celebrity tea before it hits the press. We are
              fans, just like you, and are honored to be trusted as your new ticketing provider!
            </Typography.Text>

            <div className={styles.team}>
              <Image
                src="https://img.granted.co/1655468751897-la.png"
                alt="Team location - LA"
                width={52}
                height={52}
              />
              <Typography.Text>
                We love the sun and the ocean. <br /> Located in Los Angeles, CA
              </Typography.Text>
            </div>
          </Grid>

          <img
            className={styles.image}
            src="https://img.granted.co/600x400/1655466947980-team-photo.png"
            alt="Granted team"
          />
        </Grid>

        <Grid className={styles.cards} maxWidth={720} margin="120px auto">
          <NeonCard
            iconName="question-in-circle"
            title="Faq"
            colorGlow="orange"
            description="Questions are important. Click here for our most frequently asked ones."
            after={
              <Button
                className={styles['buttons-style']}
                uppercase
                variant="primary"
                mode="external-link"
                href={FAQs}
                clr="orange"
                icon="right-arrow-with-tail"
              >
                Read our Faq
              </Button>
            }
          />

          <NeonCard
            iconName="user-plus"
            title="Careers"
            colorGlow="yellow"
            description="Teamwork makes the dream work. Check out our open positions and give us a shout!"
            after={
              <Button
                className={styles['buttons-style']}
                uppercase
                variant="primary"
                mode="external-link"
                href={CAREERS}
                icon="right-arrow-with-tail"
                clr="yellow"
              >
                Check Positions
              </Button>
            }
          />
        </Grid>
      </div>
    </Layout.Main>
    <Layout.Footer />
  </>
)

export default About
